import { ZfDialog, ZfDialogContent, ZfDialogFooter, ZfDialogHeader, ZfPill, ZfStack, ZfTextButton, ZfTooltip, ZfTypography } from '@zafire/react';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { FunctionComponent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateMaturityEvent } from '../../../api/maturity-events-api';
import { useShadowRootCustomStyles } from '../../../hooks/useShadowRootCustomStyles';
import type { BalanceSheetTableData } from '../../../interfaces/balance-sheet-table';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';

export const BalanceSheetTableRefinancingCell: FunctionComponent<ICellRendererParams<BalanceSheetTableData, BalanceSheetTableData['refinancingDetails']>> = ({
    data,
    value,
}) => {
    const { t } = useTranslation();
    const updateMaturity = useUpdateMaturityEvent();

    const tooltipRef = useRef<HTMLZfTooltipElement>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    useShadowRootCustomStyles(tooltipRef, '.tooltip { max-width: 500px !important; }');

    const onCloseDialog = () => setShowConfirmDialog(false);

    const isRefinancingRequested = value?.refinancingRequested ?? false;

    const onRefinancingChangeConfirm = () => {
        if (!data?.qualifiedId || !data?.qualifiedKey) return;

        updateMaturity.mutate(
            {
                qualifiedId: data.qualifiedId,
                qualifiedKey: data.qualifiedKey,
                refinancing: !isRefinancingRequested,
            },
            {
                onSuccess: () => showSuccessToast(t('yourChangesHaveBeenSaved')),
                onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainFailedRefinancingUpdate') }),
                onSettled: onCloseDialog,
            },
        );
    };

    if (!data?.refinancingDetails?.refinancingEligible) return null;

    return (
        <>
            <ZfTooltip text={t('wealthRefinancingTooltip')} ref={tooltipRef} style={{ maxWidth: '500px' }}>
                <ZfPill
                    slot="anchor"
                    color={isRefinancingRequested ? 'info' : 'neutral'}
                    variant="soft"
                    size="medium"
                    clickable={true}
                    onClick={() => setShowConfirmDialog(true)}
                >
                    {t(isRefinancingRequested ? 'wealthRefinancingYes' : 'wealthRefinancingNo')}
                </ZfPill>
            </ZfTooltip>
            <ZfDialog show={showConfirmDialog} size="s" onDialogClose={onCloseDialog}>
                <ZfDialogHeader>{t('refinancingConfirmDialogTitle')}</ZfDialogHeader>
                <ZfDialogContent>
                    <ZfTypography variant="body-medium" style={{ whiteSpace: 'break-spaces' }}>
                        {t(isRefinancingRequested ? 'refinancingConfirmDialogTextNo' : 'refinancingConfirmDialogTextYes')}
                    </ZfTypography>
                </ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="primary" direction="row" spacing="small">
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={onCloseDialog}>
                            {t('refinancingConfirmDialogCancel')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" hierarchy="primary" onClick={onRefinancingChangeConfirm}>
                            {t('refinancingConfirmDialogContinue')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
        </>
    );
};
